/**
 *  ・クラス名
 *    共通モデル
 *  ・概要
 *    画面共通で使用可能な処理を記述
 *  ・更新履歴
 *    2021/01/15 新規作成 富本
 *    2021/01/29 動的フォーム処理関連処理追加 佐藤
 */
import { DateTime } from 'luxon';
import HexabaseApi from '@/services/hexabase/hexabaseApi';
import authState from '@/store/auth';
import {
  GetItemDetailsResponse,
  CreateItemResponse,
  UpdateItemResponse,
  DeleteItemResponse,
  ExecuteActionResponse,
  GetAutoNumberResponse,
  DatastoreFields,
  FieldChildrenProp,
  FieldsInfo,
  DatastoreActons,
  GetInputFieldsActons,
  GetItemsResponse,
  GetItemSearch
} from '@/services/hexabase/types';
import {
  masterControlColums,
  formMapDefineData,
  formMapPropsDefineData
} from '@/constants/form/form';
import { searchFormConstruct } from '@/constants/form/search';
import { errorConf } from '@/constants/util';
import { SelectProps } from '@/constants/form/types';

/* eslint-disable @typescript-eslint/camelcase */
export interface Payload {
  fields?: [];

  conditions?: {
    id: string;
    search_value: Array<string>;
  }[];
  sort_fields?: [
    {
      id: string;
      order: 'desc' | 'asc';
    }
  ];
  per_page?: number;
  page?: number;
  use_display_id?: boolean;
}

export default class BaseModel {
  fields?: any; // 各ドメイン、マスタモデル項目に合わせてサブクラスで上書き
  detailFields?: any;

  payload?: Payload;
  private hexaApi = new HexabaseApi();
  // API レスポンス型不正時エラー番号
  private typeDifferentCode = '001';

  constructor(payload?: Payload) {
    const defaultPayload = {
      conditions: [],
      per_page: 0,
      page: 0,
      use_display_id: true
    };
    this.payload = payload ? payload : defaultPayload;
  }

  /**
   * アイテムの詳細情報（関連データ含む）を取得します
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @returns {
   *      "title": "タスクE",
   *      "field_values": {
   *          "AutoNo": {
   *              "field_id": "AutoNo",
   *              "field_name": "AutoNo",
   *              "dataType": "autonum",
   *              "value": "21",
   *              "use_as_search": true,
   *              "show_in_list": true
   *          },
   *      },
   *      "status_list": {
   *          "5cd3bde284f4be5808a6b3ac": {
   *              "status_id": "5cd3bde284f4be5808a6b3ac",
   *              "status_name": "完了"
   *          },
   *      },
   *      "status_actions": {
   *          "5cd3bde284f4be5808a6b3b1": {
   *              "action_id": "5cd3bde284f4be5808a6b3b1",
   *              "action_name": "次のステータスに進める",
   *              "display_order": 0,
   *              "crud_type": "2",
   *              "next_status_id": "5cd3bde284f4be5808a6b3ae"
   *          }
   *      },
   *      "item_actions": {
   *          "5cd3bde384f4be5808a6b3b9": {
   *              "action_id": "5cd3bde384f4be5808a6b3b9",
   *              "action_name": "内容を更新する",
   *              "display_order": 0,
   *              "crud_type": "2"
   *          },
   *      },
   *      "linked_items": {
   *          "ChildDB1": {
   *              "d_id": "5cc25d1e84f4be574418d580",
   *              "ds_name": "ChildDB1",
   *              "display_id": "ChildDB1",
   *              "fields": {
   *                  "Fld-1hfacFJP": {
   *                      "access_keys": [
   *                          "5c6363da84f4be7de035044a",
   *                          "5c6363da84f4be7de035044c",
   *                          "5ca5561484f4be19cc01d378",
   *                          "5ca5562084f4be19cc01d37a"
   *                      ],
   *                      "as_title": false,
   *                      "calc_info": {
   *                          "calc_target_fields": [
   *                              "5cc4dbc084f4be926c491d97",
   *                              "5cc4dbcd84f4be926c491d98"
   *                          ],
   *                          "formula": "{Fld-xGx9u6A2}   /   {Fld-fAcrbFfs}+{Fld-xGx9u6A2}",
   *                          "no_comma": false,
   *                          "prefix": "\\",
   *                          "suffix": ""
   *                      },
   *                      "d_id": "5cc25d1e84f4be574418d580",
   *                      "dataType": "calc",
   *                      "display_id": "Fld-1hfacFJP",
   *                      "f_id": "5cc8fa5484f4be926c491d9a",
   *                      "fieldIndex": 0,
   *                      "field_csv_name": "",
   *                      "full_text": false,
   *                      "hideOnInput": false,
   *                      "id": "5cc8fa5484f4be926c491d9a",
   *                      "max_value": "",
   *                      "min_value": "",
   *                      "name": "計算式フィールド",
   *                      "names": {
   *                          "en": "計算式フィールド",
   *                          "ja": "計算式フィールド"
   *                      },
   *                      "p_id": "5c6363d984f4be7de0350445",
   *                      "search": true,
   *                      "show_list": true,
   *                      "status": false,
   *                      "title_order": 0,
   *                      "unique": false,
   *                      "w_id": "5c5fa7da84f4be4250aaee27"
   *                  },
   *              },
   *              "items": []
   *          },
   *          "FieldTypeLineItems": {
   *              "d_id": "5c6d32f784f4be1f241ff9ff",
   *              "ds_name": "フィールドType明細",
   *              "display_id": "FieldTypeLineItems",
   *              "fields": [
   *                  "FK": {
   *                      "access_keys": [
   *                          "5c6363da84f4be7de035044a",
   *                          "5c6363da84f4be7de035044c"
   *                      ],
   *                      "as_title": true,
   *                      "d_id": "5c6d32f784f4be1f241ff9ff",
   *                      "dataType": "text",
   *                      "display_id": "FK",
   *                      "f_id": "5c6d32f784f4be70b8bd20b9",
   *                      "fieldIndex": 0,
   *                      "field_csv_name": "",
   *                      "full_text": true,
   *                      "hideOnInput": false,
   *                      "id": "5c6d32f784f4be70b8bd20b9",
   *                      "max_value": "",
   *                      "min_value": "",
   *                      "name": "外部キー",
   *                      "names": {
   *                          "en": "FK",
   *                          "ja": "外部キー"
   *                      },
   *                      "p_id": "5c6363d984f4be7de0350445",
   *                      "search": true,
   *                      "show_list": true,
   *                      "status": false,
   *                      "title_order": 3,
   *                      "unique": false,
   *                      "w_id": "5c5fa7da84f4be4250aaee27"
   *                  },
   *              ],
   *              "items": [
   *                  {
   *                      "FK": "タスクE",
   *                      "Fld-677Ld02p": "h.iwasaki,BeeBot",
   *                      "Fld-BLoaNKjo": "A",
   *                      "Fld-NdC29Im0": "2015-12-31T15:00:00Z",
   *                      "Fld-d0g8UE7D": "X",
   *                      "Fld-xjKEyppI": "確認",
   *                      "Fld-zto1YlDw": "AAAAA,DDDDD",
   *                      "created_at": "2019-04-22T08:05:45Z",
   *                      "created_by": "IMPORT",
   *                      "d_id": "5c6d32f784f4be1f241ff9ff",
   *                      "i_id": "5cbd75d984f4bef6c0ed8591",
   *                      "labels": "",
   *                      "p_id": "5c6363d984f4be7de0350445",
   *                      "rev_no": "2",
   *                      "status_id": "5c6d32f884f4be70b8bd20c2",
   *                      "title": "タスクE",
   *                      "updated_at": "2020-05-23T07:48:16Z",
   *                      "updated_by": "5c5fa7aa84f4be4250aaee25"
   *                  }
   *              ]
   *          }
   *      }
   *  }
   */
  public getLinkedItems(applicationId: string, datastoreId: string, itemId: string) {
    return this.hexaApi
      .getItemDetails(authState.token, applicationId, datastoreId, itemId, true)
      .then(response => {
        if (this.isItemDetailType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }
  
  public getLinked(applicationId: string, datastoreId: string, itemId: string) {
    return this.hexaApi
      .getLinkedItemList(authState.token, applicationId, datastoreId, itemId)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  public getItemDetails(applicationId: string, datastoreId: string, itemId: string, format?: string) {
    return this.hexaApi
      .getItemDetails(authState.token, applicationId, datastoreId, itemId, false, format)
      .then(response => {
        if (format !== undefined){
          return response.data;
        } else if (this.isItemDetailType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * アイテム・関連データを新規に登録します
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param params - データ作成用パラメータ
   * @returns {
   *      "error": null,
   *      "history_id": "5d661782aa39559a80479492",
   *      "item_id": "5a2647410e24792d87451e34",
   *      "item": {
   *         // Payload に "return_item_result": true を指定した場合、登録されたアイテム情報がもどる
   *      }
   *  }
   */
  public registerLinkedItems(applicationId: string, datastoreId: string, params: any) {
    return this.hexaApi
      .registerItem(authState.token, applicationId, datastoreId, params)
      .then(response => {
        if (this.isCreateItemType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 対象アイテム・関連データの更新を行います
   * 関連データは既存データを削除し新たにインサートします
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @param params - データ更新用パラメータ
   * @param linkDatastoreId - 関連データストアID
   * @returns {
   *    item: {
   *      // 登録されたアイテム情報がもどる（関連アイテムの詳細は含まれない）
   *    }
   *  }
   */
  updateLinkedItems(
    applicationId: string,
    datastoreId: string,
    itemId: string,
    params: any,
    linkDatastoreId: string
  ) {
    // 関連データを対象データストアに問い合わせ後削除処理
    return this.hexaApi
      .getLinkedItemList(authState.token, applicationId, datastoreId, itemId)
      .then(response => {
        const linkData: Array<{ [k: string]: string }> =
          response.data.result[linkDatastoreId].items;
        if (linkData && linkData.length !== 0) {
          const linkItems: string[] = linkData.map(item => {
            return item.i_id;
          });
          linkItems.forEach(itemId => {
            params.related_ds_items[linkDatastoreId].push({ operation: 3, i_id: itemId });
          });
          return this.hexaApi
            .editItemData(authState.token, applicationId, datastoreId, itemId, params)
            .then(response => {
              return response.data as UpdateItemResponse;
            });
        }
      });
  }

  /**
   * 指定アイテム及び関連データを削除します
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @param linkedDatastoreIdList - 削除対象の関連データストアID群
   * @returns {
   *      "error": null,
   *  }
   */
  public deleteLinkedItems(
    applicationId: string,
    datastoreId: string,
    itemId: string,
    linkedDatastoreIdList: string[]
  ) {
    return this.hexaApi
      .deleteItems(authState.token, applicationId, datastoreId, itemId, linkedDatastoreIdList)
      .then(response => {
        if (this.isDeleteItemType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 対象アイテム・関連データの更新を行います
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @param params - データ更新用パラメータ
   * @returns {
   *    item: {
   *      // 登録されたアイテム情報がもどる（関連アイテムの詳細は含まれない）
   *    }
   *  }
   */
  updateItems(
    applicationId: string,
    datastoreId: string,
    itemId: string,
    params: any
  ) {
  // 関連データを対象データストアに問い合わせ後削除処理
  return this.hexaApi
    .editItemData(authState.token, applicationId, datastoreId, itemId, params)
    .then(response => {
      return response.data as UpdateItemResponse;
    });
  }

  /**
   * 指定したアイテムのステータスを進めます
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @param actionId - 実行アクションID（display_id）
   * @param param - アクション実行時必要パラメーター  ※必須「rev_no」
   * TODO: レスポンス型指定についてはAPI改修後反映
   */
  public changeStatus(
    applicationId: string,
    datastoreId: string,
    itemId: string,
    actionId: string,
    params: any
  ) {
    return this.hexaApi
      .executeAction(authState.token, applicationId, datastoreId, itemId, actionId, params)
      .then(response => response.data as ExecuteActionResponse)
      .catch(error => {
        throw new Error(error.message);
      });
  }


  /**
   * 指定したアイテムで指定したアクションを行う
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @param actionId - 実行アクションID（display_id）
   * @param param - アクション実行時必要パラメーター  ※必須「rev_no」
   * TODO: レスポンス型指定についてはAPI改修後反映
   */
  public executeAction(
    applicationId: string,
    datastoreId: string,
    itemId: string,
    actionId: string,
    params: any
  ) {
    return this.hexaApi
      .executeAction(authState.token, applicationId, datastoreId, itemId, actionId, params)
      .then(response => {
        // TODO: APIのレスポンスを確認後処理のちほど追加（レスポンス問い合わせ中）
        return response;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 自動採番APIに問い合わせします
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param fieldId - データストア内参照連番フィールドのID
   * @param param - 採番ルール { branch_key:付与するSubPrefix{string}, zero_padding:番号をゼロ埋めする{boolean} , digit:桁数{number} }
   * @returns {
   *    "has_error": false,
   *      "result": {
   *        "number": 1,
   *        "value": "A00001"
   *    }
   * }
   */
  public getAutoNumber(
    applicationId: string,
    datastoreId: string,
    fieldId: string,
    param: { [k: string]: string }
  ) {
    return this.hexaApi
      .getAutoNumberApi(authState.token, applicationId, datastoreId, fieldId, param)
      .then(response => {
        if (this.isAutoNumType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 特定アイテムの次ステータスリストを取得します
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param itemId - 対象アイテムID
   * @returns [
   *    "5cd3bde284f4be5808a6b3b1": {
   *        "action_id": "5cd3bde284f4be5808a6b3b1",
   *        "action_name": "次のステータスに進める",
   *        "display_order": 0,
   *        "crud_type": "2",
   *        "next_status_id": "5cd3bde284f4be5808a6b3ae"
   *    }
   *  ]
   */
  public getExecutableStatusAction(applicationId: string, datastoreId: string, itemId: string) {
    return this.hexaApi
      .getItemDetails(authState.token, applicationId, datastoreId, itemId, false)
      .then(response => {
        if (this.isItemDetailType(response.data)) {
          return response.data.status_actions && response.data.status_actions.length !== 0
            ? response.data.status_actions
            : [];
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 指定データストアのフィールド情報を取得します
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param applicationId - アプリケーションID
   * @param datastoreId - 対象データストアID
   * @returns {
   *    "fields": {
   *        "59bf42550e247918255de00d": {
   *            "field_id": "59bf42550e247918255de00d",
   *            "name": "タイトル",
   *            "display_id": "タイトル",
   *            "dataType": "text",
   *            "search": true,
   *            "show_list": false,
   *            "as_title": true,
   *            "status": false,
   *            "fieldIndex": 0,
   *            "title_order": 1,
   *            "full_text": false,
   *            "unique": false,
   *            "min_value": "",
   *            "max_value": ""
   *          },
   *        },
   *        "5a8392390e247948bd5cfaf3": {
   *          "field_id": "5a8392390e247948bd5cfaf3",
   *          "name": "s1",
   *          "display_id": "Fld-FZR9lYNR",
   *          "dataType": "select",
   *          "search": true,
   *          "show_list": true,
   *          "as_title": false,
   *          "status": false,
   *          "fieldIndex": 0,
   *          "title_order": 0,
   *          "full_text": false,
   *          "unique": false,
   *          "min_value": "",
   *          "max_value": "",
   *          "options": [
   *            {
   *              "option_id": "dc236a0d-58b0-400e-88c7-9276207438cb",
   *              "sort_id": 0,
   *              "value": "選択1",
   *              "enabled": true
   *            },
   *            {
   *              "option_id": "a355531b-c80f-40e8-b6d7-3dc75cc9f3c5",
   *              "sort_id": 1,
   *              "value": "選択2",
   *              "enabled": true
   *            }
   *          ]
   *        }
   *    },
   *    "field_layout": {
   *        "59bf42550e247918255de00d": {
   *            "field_id": "59bf42550e247918255de00d",
   *            "sizeX": 9,
   *            "sizeY": 1,
   *            "col": 0,
   *            "row": 0
   *        },
   *        "5a26722e0e24794c979fa5b6": {
   *            "field_id": "5a26722e0e24794c979fa5b6",
   *            "sizeX": 6,
   *            "sizeY": 1,
   *            "col": 0,
   *            "row": 1
   *        },
   *    }
   * }
   */
  public getFieldsData(applicationId: string, datastoreId: string) {
    return this.hexaApi
      .getDatastoreFields(authState.token, applicationId, datastoreId)
      .then(response => {
        if (this.isFieldsType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 特定データストアの新規登録アクションを取得します
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * 注意：Datastoreの項目ID使用不可
   * @param datastoreId - 対象データストアID
   * @returns [
   *    "actions": [
   *        {
   *            "action_id": "5a2502f00e24792b67887200",  // アクションID
   *            "display_order": 2,  // メニューの表示順。HexabaseのActionメニューのソート順
   *            "crud_type": "1",  // 常に1（新規登録）が返る
   *            "next_status_id": "5a2502f00e24792b678871f8"   //アクション実行後、遷移するステータスID
   *        }
   *    ]
   */
  public getNewActionList(datastoreId: string) {
    return this.hexaApi
      .getNewActions(authState.token, datastoreId)
      .then(response => {
        if (this.isActionListType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 特定データストアのアクションで利用可能なフィールド情報を取得する
   * それぞれ「新規」「更新」にて必須項目・項目の表示等が変わるため
   * 画面項目毎の設定を取得する
   * エラーについて：
   * 型不正・サーバーエラー等が発生した場合はエクセプションを投げるため
   * 参照元で try/catch にてエラーハンドリングを行ってください
   * @param datastoreId - 対象データストアID
   * @param actionId    - アクションID
   * @returns {
   *    "action": {
   *        "action_id": "5a2671ec0e24794c979fa5b1",
   *        "display_order": 3,
   *        "description": "",
   *        "crud_type": "2",
   *        "next_status_id": "5a2671eb0e24794c979fa5ab"
   *    },
   *    "action_fields": {
   *        "5a2671eb0e24794c979fa5a9": {
   *            "field_id": "5a2671eb0e24794c979fa5a9",
   *            "name": "タイトル",
   *            "display_id": "Fld-U2WtgeXa",
   *            "dataType": "text",
   *            "search": true,
   *            "show_list": false,
   *            "as_title": true,
   *            "status": false,
   *            "fieldIndex": 0,
   *            "title_order": 1,
   *            "full_text": false,
   *            "unique": false,
   *            "min_value": "",
   *            "max_value": ""
   *        }
   *    },
   *    "action_field_settings": {
   *      "5a2671eb0e24794c979fa5a9": {
   *          "action_id": "5a2671ec0e24794c979fa5b1",
   *          "field_id": "5a2671eb0e24794c979fa5a9",
   *          "show": true,
   *          "update": true,
   *          "mandatory": false
   *      }
   *    }
   *  }
   */
  public getFieldsByActionId(datastoreId: string, actionId: string) {
    return this.hexaApi
      .getInputFieldsByActionId(authState.token, datastoreId, actionId)
      .then(response => {
        if (this.isInputFieldByActionType(response.data)) {
          return response.data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 特定データストアからアイテム情報取得
   * @param applicationId - 対象アプリケーションID
   * @param dataStoreId - 対象データストアID
   * @returns {
   *      "items": [
   *          {
   *              "58bbaa27fbfcba773851339f": "fadfa",
   *              "58bbaa27fbfcba77385133a2": "新規",
   *              "_id": "596e2327fbfcbab8283dde09",
   *              "a_id": "58bbaa27fbfcba77385133ac",
   *              "access_keys": "",
   *              "created_at": "2017-07-19T00:03:06+09:00",
   *              "created_by": "58272f4efb90a148d8508d9c",
   *              "d_id": "58bbaa27fbfcba6098746061",
   *              "i_id": "596e2327fbfcbab8283dde09",
   *              "p_id": "586f6c2ccce5fe6ad071b1d4",
   *              "rev_no": "1",
   *              "status_id": "58bbaa27fbfcba77385133a4",
   *              "title": "fadfa",
   *              "unread": "0"
   *          }
   *      ],
   *      "totalItems": 2
   *  }
   */
  public getItemList(applicationId: string, dataStoreId: string, params: any) {
    return this.hexaApi
      .getItems(authState.token, applicationId, dataStoreId, params)
      .then(response => {
        return response;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  public getItemSearch(applicationId: string, dataStoreId: string, params: any) {
    return this.hexaApi
      .getItemList(authState.token, applicationId, dataStoreId, params)
      .then(response => {
        return response.data as GetItemsResponse;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  public getItemSearchConditions(applicationId: string, dataStoreId: string) {
    return this.hexaApi
      .getItemSearchConditions(authState.token, applicationId, dataStoreId)
      .then(response => {
        return response as GetItemSearch;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /***
   * 以下入力フォーム動的生成メソッド
   * データストアのフィールド情報を取得し
   * HexaBaseUIで設定した入力フォームを画面上で入力フォームとして再現する
   */

  /**
   * 動的生成コンポーネントに渡すオブジェクト（設定）を生成
   * @param applicationId - 対象アプリケーションID
   * @param datastoreId - 対象データストアID
   * @param curentData - 登録済みアイテム情報 入力値に反映させる場合使用
   * @returns 各フィールド情報を含んだ配列
   * [
   *  {
   *    as_title: true
   *    col: 0
   *    component: "InputArea"
   *    dataType: "text"
   *    display_id: "order_application_no"
   *    fieldIndex: 0
   *    field_id: "60050d23eb0c9000012d15ee"
   *    full_text: false
   *    max_value: ""
   *    min_value: ""
   *    name: "発注申請No"
   *    props: {__ob__: Observer}
   *    row: 0
   *    search: true
   *    show_list: true
   *    sizeX: 2
   *    sizeY: 1
   *    status: false
   *    title_order: 4
   *    unique: false
   *  }
   *]
   */
  makeFieldsList(applicationId: string, datastoreId: string, curentData?: Array<FieldsInfo>) {
    // 該当データストアのフィールドデータを取得
    return this.getFieldsData(applicationId, datastoreId).then(response => {
      const result: any = [];
      // 各フィールド情報を取得し適用するモジュールコンポーネントとprops情報を追加
      Object.keys(response.fields).forEach(key => {
        const copyObj = Object.create(response);
        const fieldType = copyObj.fields[key].dataType;
        if (fieldType in formMapDefineData) {
          copyObj.fields[key]['component'] = formMapDefineData[fieldType].component;
          copyObj.fields[key]['props'] = this.makeProps(
            formMapDefineData[fieldType].props,
            copyObj.fields[key],
            curentData
          );
          // レイアウト情報をセットする
          if (key in copyObj.field_layout) {
            copyObj.fields[key] = { ...copyObj.fields[key], ...copyObj.field_layout[key] };
          }
          result.push(copyObj.fields[key]);
        }
      });
      return this.getLayoutKeySort(result);
    });
  }

  /**
   * APIから取得した「datatype」をみてフロントで使用する
   * コンポーネント情報（各コンポーネント毎に渡す値）をマッピングする
   * @param props - コンポーネントに渡すべきプロパティー
   * @param fieldInfo - フィールド情報
   * @param curentData - アイテム情報
   * @returns コンポーネントProps { [k: string]: string }
   */
  private makeProps(props: string[], fieldInfo: FieldChildrenProp, curentData?: Array<FieldsInfo>) {
    const result: { [k: string]: any } = {};
    const fieldsData = fieldInfo as { [k: string]: any };
    props.forEach(prop => {
      if (prop in formMapPropsDefineData) {
        const setFieldKey = formMapPropsDefineData[prop];
        if (prop === 'selectData') {
          // select component に渡すoptionリストを加工
          const selectOptions: Array<{ [k: string]: string | number }> = fieldsData[setFieldKey];
          if (selectOptions === undefined) {
            return;
          }

          selectOptions
            .sort((a, b) => {
              return a.sort_id > b.sort_id ? 1 : -1;
            })
            .map(option => {
              option['text'] = option['value'];
              option['value'] = option['option_id'];
              return option;
            });
          result[prop] = { name: fieldInfo['display_id'], options: selectOptions };
        } else {
          result[prop] = fieldsData[setFieldKey];
        }
      }
    });
    // 既存アイテム情報がある場合はデータセット
    if (curentData !== null) {
      const currentFieldData = curentData?.find(v => v.field_id === fieldInfo.display_id);
      result['value'] = currentFieldData?.value;
    }
    return result;
  }

  /**
   * フロントに描画する際のコンポーネント並び順をHexaBaseUIに合わせます
   * @param fieldList - フィールド情報
   * @returns 「row」「col」で指定したフィールド情報
   */
  private getLayoutKeySort(fieldList: Array<FieldChildrenProp>) {
    return fieldList.sort((a, b) => {
      if ('row' in a && 'col' in b) {
        if (
          typeof a['row'] === 'number' &&
          typeof a['col'] === 'number' &&
          typeof b['row'] === 'number' &&
          typeof b['col'] === 'number'
        ) {
          return a['row'] - b['row'] || a['col'] - b['col'];
        }
      }
      return 0;
    });
  }

  /**
   * フィールドに対して任意で指定したコンポーネント使用する場合の
   * 定義ファイルを読み取り使用コンポーネント情報に上書きをする
   * @param fields - フィールド情報
   * @param addFields - 任意コンポーネント定義オブジェクト
   * @returns フィールド情報に任意コンポーネント定義を上書きしたオブジェクト
   */
  public addOptionalFormField(
    fields: Array<FieldChildrenProp>,
    addFields: Array<{ [k: string]: any }>
  ) {
    return fields.map(field => {
      const matchField = addFields.find(v => v.display_id === field.display_id);
      // propsがObjectの為先にマージしておく
      if (matchField !== undefined && 'props' in matchField) {
        matchField['props'] = { ...field['props'], ...matchField['props'] };
      }
      return matchField !== null ? { ...field, ...matchField } : field;
    });
  }

  /**
   * GetInputFields APIからの情報を確認し
   * アクション毎に「表示する要素か」「更新可能か」「必須項目か」
   * 判別するための情報をフィールド情報に追加
   * @param field - 単体フィールド情報
   * @param inputFields - GetInputFields APIから取得したアクション毎のフィールド情報
   */
  public inputRestriction(field: FieldChildrenProp, inputFields: GetInputFieldsActons) {
    if (field.field_id in inputFields.action_field_settings) {
      const actionFields = inputFields.action_fields[field.field_id];
      const fieldSetting = inputFields.action_field_settings[field.field_id];
      if ('props' in field && !fieldSetting.update) {
        // backendで更新不可に設定されている場合入力要素をdisableに変更
        field['props']!['isDisabled'] = true;
      }
      if ('props' in field && fieldSetting.mandatory) {
        // backendで必須項目に設定されている場合入力要素のrulesにrequiredを追加
        field['props']!['rules'] = 'required';
      }
      if ('props' in field && actionFields.min_value !== '') {
        const minVal = Number(actionFields.min_value);
        if (!Number.isNaN(minVal)) {
          // backendで最小値が指定されている場合で、数値変換ができた場合入力要素のrulesにmin_valueを追加
          if (field['props']!['rules'] === undefined || field['props']!['rules'] === '') {
            field['props']!['rules'] = `min_value:${minVal}`;
          } else {
            field['props']!['rules'] += `|min_value:${minVal}`;
          }
        }
      }
      field = {
        ...field,
        ...{
          show: fieldSetting.show,
          update: fieldSetting.update,
          mandatory: fieldSetting.mandatory
        }
      };
    }
    return field;
  }

  /**
   * マスタコントロールコンポーネントに使用するColumnデータをセットします
   * セットしているデータの体裁（並び順）を整えます
   * @param data - コンポーネントに渡すマスタデータ
   * @param sortKey - マスターデータの並び替えを行う際のデータkey
   * @param masterName - 使用するマスタ名
   */
  public modifyMasterData(data: any, sortKey: string, masterName: string) {
    const result: { [k: string]: Array<{ [k: string]: string }> } = {};
    if (data !== undefined) {
      result['columns'] = masterName !== undefined ? masterControlColums[masterName] : [];
      result['body'] = this.createSelectType(data, sortKey);
    }
    return result;
  }

  /**
   * セレクトオプションの並び順を変更
   * @param array - セレクトオプション配列
   * @param sortKey - 並び替えkey値
   * @returns セレクトオプション配列
   */
  public createSelectType(
    array: Array<{ [k: string]: string }>,
    sortKey: string
  ): Array<{ [k: string]: string }> {
    if (array.length === 0) return [];
    return array.sort((a, b) => {
      return a[sortKey] < b[sortKey] ? -1 : 1;
    });
  }

  /**
   * 最大Grid数を取得するためのレコード取得
   * @param fieldsInfo - フィールド情報
   * @returns GridColumn数を一番使用している単体フィールド情報
   */
  public getMaxcolRecode(fieldsInfo: Array<FieldChildrenProp>) {
    const maxColRecode = fieldsInfo.reduce((a, b) => {
      if (
        typeof a.col === 'number' &&
        typeof b.col === 'number' &&
        typeof a.sizeX === 'number' &&
        typeof b.sizeX === 'number'
      ) {
        return a.col + a.sizeX > b.col + b.sizeX ? a : b;
      }
      return {} as FieldChildrenProp;
    });
    return maxColRecode;
  }

  /**
   * マスター参照系コンポーネントにマスターデータをセットする
   * @param field - フィールド情報
   * @param masterData - マスターデータ
   * @returns マスター情報をセットしたフィールド情報
   */
  addOptionalMasterData(
    field: FieldChildrenProp,
    masterData: { [k: string]: { [k: string]: Array<{ [k: string]: string }> } }
  ) {
    if ('dblookup' in field && field.dblookup !== undefined) {
      if (
        field.component === 'MasterSelectControl' ||
        field.component === 'MasterSelectSetControl' ||
        field.component === 'MasterSelectMultiControl' ||
        field.component === 'MasterSelectMultiSetControl' ||
        field.component === 'MasterSelectButton'
      ) {
        field.props!['columns'] = masterData[field.dblookup]['columns'];
        field.props!['body'] = masterData[field.dblookup]['body'];
      } else if (
        field.component === 'SelectArea' ||
        field.component === 'SelectComboBoxArea' ||
        field.component === 'InputUnitPriceQuantityCalculation'
      ) {
        field.props!['selectData'] = {
          name: field.display_id,
          options: masterData[field.dblookup].body.map((v) => {
            const text = field.valueColum === undefined ? '' : v[field.valueColum];
            const dataKey = field.dataKeyColum === undefined ? '' : v[field.dataKeyColum];
            return { value: v.id, text: text, key: dataKey };
          }),
        };
      }
    }
    return field;
  }

  /**
   * コンポーネント間で選択肢によって
   * マスター情報を絞り込む際に使用（※セレクトのみ対応）
   * 例）品目3 -> 規格コンポーネント内マスタ絞込み表示
   * @param fieldData - フィールド情報
   * @param masterData - マスターデータ
   * @param inputRes - インプット要素
   * @param keyDisplayId - マスタを絞り込む元となる画面項目名
   * @param targetDisplayId - 絞り込む対象となる画面項目名
   * @returns array - 絞込みを行ったフィールドデータ
   */
  public getNarrowDownSelectOptin(
    fieldData: Array<FieldChildrenProp>,
    masterData: { [k: string]: { [k: string]: Array<{ [k: string]: string }> } },
    inputRes:{[k: string]: string},
    keyDisplayId: string,
    targetDisplayId: string
  ): Array<FieldChildrenProp>{
    if (inputRes.name === keyDisplayId) {
      const deep = fieldData.findIndex((v) => v.display_id === targetDisplayId);
      const initialField = this.addOptionalMasterData(fieldData[deep], masterData);
      const selectOptions: Array<{ [k: string]: string }> = initialField['props']!['selectData']['options'];
      const optionCount = selectOptions.length;
      const filtedOption =
      inputRes.value.length !== 0
          ? selectOptions.filter((v) => v.key === inputRes.value)
          : selectOptions;
      fieldData[deep]['props']!['selectData']['options'].splice(
        0,
        optionCount,
        ...filtedOption
      );
      fieldData[deep]['props']!['InputValue'] = '';
      fieldData[deep]['props']!['selectValue'] = '';
      fieldData[deep]['props']!['isDisabled'] = inputRes.value.length === 0 ? true : false;
    }
    return fieldData;
  }

  /**
   * 明細情報入力欄の初期登録BOX生成
   * @param initialData - 明細入力フォームの初期設定
   * @returns array 明細入力フォーム設定値配列
   */
  public createInitialDetailBoxGeneric(
    initialData: Array<FieldChildrenProp>,
    num = 1
  ): Array<Array<FieldChildrenProp>> {
    const detailAreas: Array<Array<FieldChildrenProp>> = [];
    for (let i = 0; i < num; i++) {
      detailAreas.push(JSON.parse(JSON.stringify(initialData)));
    }
    for (let i = 0; i < num; i++) {
      detailAreas[i].forEach((val, index) => {
        if ('props' in detailAreas[i][index] && detailAreas[i][index]['props'] !== undefined) {
          detailAreas[i][index]['props']!['name'] = `${
            detailAreas[i][index]['props']!['name']
          }[${i}]`;
        }
      });
    }
    return detailAreas;
  }

  /**
   * 発注・納品明細情報入力欄の初期登録BOX生成
   * @param initialData - 明細入力フォームの初期設定
   * @returns array 明細入力フォーム設定値配列
   */
  public createInitialDetailBox(
    initialData: Array<FieldChildrenProp>
  ): Array<Array<FieldChildrenProp>> {
    const detailAreas:Array<Array<FieldChildrenProp>> = [];
    detailAreas.push(JSON.parse(JSON.stringify(initialData)))
    detailAreas.push(JSON.parse(JSON.stringify(initialData)))
    detailAreas.push(JSON.parse(JSON.stringify(initialData)))     
    for(let i = 0;i<3;i++) {
      detailAreas[i].forEach((val, index) => {
        if ('props' in detailAreas[i][index] && detailAreas[i][index]['props'] !== undefined) {
          detailAreas[i][index]['props']!['name'] = `${detailAreas[i][index]['props']!['name']}[${i}]`
        }
      });
    }
    return detailAreas;
  }

  /**
   * Selectコンポーネントで使用できるように整形
   * @module setSelectItemArrayFormat
   * @param Array<any> items - 整形に使用するベースデータ
   * @param string findItemName - フィルタ時に使用するプロパティ
   * @param string setItemValue - 整形時にValue側に設定される値のプロパティ 
   * @param string setItemText - 整形時にText側に設定される値のプロパティ
   * @return selectItemData - 整形したデータ
   */
  public setSelectItemArrayFormat(items: Array<any>, findItemName: string, setItemValue: string, setItemText: string) {
    let ArrayData = [];
    const selectItemData:SelectProps = { name: '', options: [] };
    ArrayData = items.filter((v1, i1, a1) => {
      return a1.findIndex(v => v1[findItemName] === v[findItemName]) === i1
    })

    for( const item of ArrayData ) {
      selectItemData.options.push({ value: item[setItemValue], text: item[setItemText] });
    }
    return selectItemData;
  }

  /**
   * 編集画面にて登録されている明細データ分の
   * 明細情報入力フィールドを表示するための設定情報を加工
   * @param {any} currentDetailData - 登録されているデータ配列 {[k: string]: string}[]
   * @param {array} defaultData - 明細情報入力エリアの基本設定
   * @returns {array} 明細情報入力エリアのに登録情報をマージした配列情報 Array<Array<FieldChildrenProp>>
   */
  public getCurrentDetailArea(currentDetailData: any, defaultData: Array<FieldChildrenProp>) {
    if (currentDetailData === undefined || currentDetailData.length === 0) return [];
    const currentData = JSON.parse(JSON.stringify(currentDetailData));
    const currentItemData: Array<{ [k: string]: string }> = currentData['items'];
    if (currentItemData === undefined || currentItemData.length === 0) return [];
    const detailAreas: Array<Array<FieldChildrenProp>> = [];
    for (let i = 0; i < currentItemData.length; i++) {
      detailAreas.push(JSON.parse(JSON.stringify(defaultData)));
    }
    if (detailAreas.length === 0) return [];
    detailAreas.map((detail, i) => {
      detail.map((field, key) => {
        field['props']!['name'] = `${field['props']!['name']}[${i}]`;
        if (currentItemData[i] && currentItemData[i][field['field_id']]) {
          field['props']!['value'] = currentItemData[i][field['field_id']];
        }
        return field;
      });
      return detail;
    });
    return detailAreas;
  }

  /**
   * 明細の登録情報に消費税・金額計算し追加
   * @param {array} registData - DB登録用配列
   * @param {array} taxMaster - 課税マスタ
   * @returns {array} registData - 消費税・金額計算し追加したDB登録用配列
   */
  public setDetailsAmountProps( registData: Array<any>, taxMaster: Array<{ [k: string]: string }>) {
    const keyPrice = 'unit_price';
    const keyQuantity = 'order_quantity';
    const taxId = 'tax_class_id';
    const keyTax = 'tax'
    const KeyAmount = 'amount';
    registData.forEach((v, i) => {
      const price = v.item[keyPrice] ? Number(v.item[keyPrice]) : 0 ;
      const quantity = v.item[keyQuantity] ? Number(v.item[keyQuantity]) : 0 ;
      const taxClassId = v.item[taxId] ? v.item[taxId] : undefined ;
      registData[i].item[KeyAmount] = price*quantity;
      const taxRate = this.getTax(taxClassId, taxMaster);
      if (registData[i].item[KeyAmount] > 0) {
        registData[i].item[keyTax] = registData[i].item[KeyAmount]*taxRate;
      }
    });
    return registData;
  }

  /**
   * 消費税率を計算しやすい形に変更
   * @param taxClassId - 課税マスタID
   * @param taxMaster - 課税マスタ
   */
  public getTax(taxClassId: string, taxMaster: Array<{ [k: string]: string }>): number{
    const taxRow = taxMaster.find(v => v.id === taxClassId);
    let taxRate = 0;
    if (taxRow && taxRow['tax_rate'] !== '0'){
      taxRate = Number(taxRow['tax_rate'])/100;
    }
    return taxRate;
  }
  
  /**
   * 明細情報(Props経由)のトータル金額を計算します
   * 1明細分
   * @param {array} detailData - 明細1レコード分のProps配列
   * @readonly {number} - 数量・単価より計算した合計金額 
   */
  public getAmountDetailRow(detailData: Array<FieldChildrenProp>) {
    const unitPrice = detailData.find(v => v.display_id === 'unit_price')?.props?.value || 0;
    const orderQuantity = detailData.find(v => v.display_id === 'order_quantity')?.props?.value || 0;
    const totalPrice = Number(unitPrice) * Number(orderQuantity);
    return totalPrice;
  }

  /**
   * 特定明細(Props)の課税金額を算出
   * @param {array} detailData - 明細1レコード分のProps配列
   * @param {array} taxData - 課税マスタ
   * @param {number} price - 合計金額
   */
  public getTaxDetailRow(detailData: Array<FieldChildrenProp>, taxData: Array<{ [k: string]: string }>, price:number) {
    const totalPrice = price === undefined || typeof price !== 'number' ? 0 : price;
    const taxClassId = detailData.find(v => v.display_id === 'tax_class_id')?.props?.value;
    if (taxClassId) {
      const rate = this.getTax(taxClassId, taxData);
      return totalPrice*rate;
    }
    return 0;
  }
  
  /**
   * 登録・編集画面で使用する金額合計パネルに表示する金額を返却する
   * @param DetailData - DB登録用明細情報
   * @returns {object} - 計算した金額と消費税額
   */
  public modifyInitialSummary(DetailData: Array<{[k: string]: any}>){
    const result = { total: [0],tax: [0] };
    DetailData.forEach((recode, index) => {
      if (recode.item.amount) result['total'][index] = Number(recode.item.amount);
      if (recode.item.tax) result['tax'][index] = Number(recode.item.tax);
    });
    return result;
  }

  /**
   * Date型のフォーマット変更を行います
   * @param {string} date - 日付け文字列
   * @param {string} zone - タイムゾーン
   * @param {string} formmat - フォーマット
   * @returns string - フォーマットを変更した日付け文字列
   */
  public editDatetype(date: string, zone: string, formmat: string): string{
    if (!this.isCheckVal(date)) return '';
    const datetime = DateTime.fromISO(date).setZone(zone);
    return formmat.length === 0 ? datetime.toSQLDate() : datetime.toFormat(formmat);
  }

  public getGridStatus(data: Array<FieldChildrenProp>): string {
    const maxColRecode = this.getMaxcolRecode(data);
    const colCount = maxColRecode.col! + maxColRecode.sizeX!;
    const colWidth = 100 / colCount;
    return`${colWidth}% `.repeat(colCount);
  }

  /***
   * 以下エラー関連判別メソッド
   */

  /**
   * アイテム詳細APIのレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isItemDetailType(data: any): data is GetItemDetailsResponse {
    return (
      data !== null &&
      typeof data === 'object' &&
      typeof data.title === 'string' &&
      Array.isArray(data.field_values) &&
      (Array.isArray(data.status_list) || data.status_list === null) &&
      (Array.isArray(data.status_actions) || data.status_actions === null) &&
      Array.isArray(data.item_actions)
    );
  }

  /**
   * アイテム作成後のレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isCreateItemType(data: any): data is CreateItemResponse {
    return (
      data !== null &&
      typeof data === 'object' &&
      (typeof data.error === 'string' || data.error === null) &&
      typeof data.history_id === 'string' &&
      typeof data.item_id === 'string' &&
      typeof data.item === 'object'
    );
  }

  /**
   * アイテム削除後のレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isDeleteItemType(data: any): data is DeleteItemResponse {
    return (
      data !== null &&
      typeof data === 'object' &&
      (typeof data.error === 'string' || data.error === null)
    );
  }

  /**
   * アクション実行後のレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isExecuteActionType(data: any): data is ExecuteActionResponse {
    return data !== null && typeof data === 'object' && typeof data.item === 'object';
  }

  /**
   * アクション実行後のレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isFieldsType(data: any): data is DatastoreFields {
    return (
      data !== null &&
      typeof data === 'object' &&
      typeof data.field_layout === 'object' &&
      typeof data.fields === 'object'
    );
  }

  /**
   * 自動採番API発行後のレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isAutoNumType(data: any): data is GetAutoNumberResponse {
    return (
      data !== null &&
      typeof data === 'object' &&
      typeof data.has_error === 'boolean' &&
      typeof data.result === 'object' &&
      typeof data.result.number === 'number' &&
      typeof data.result.value === 'string'
    );
  }

  /**
   * アクションリストのレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isActionListType(data: any): data is DatastoreActons {
    return data !== null && typeof data === 'object' && typeof data.actions !== null;
  }

  /**
   * アクションリストのレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isInputFieldByActionType(data: any): data is GetInputFieldsActons {
    return (
      data !== null &&
      typeof data === 'object' &&
      typeof data.action !== null &&
      typeof data.action_fields !== null &&
      typeof data.action_field_settings !== null
    );
  }

  /**
   * アクションリストのレスポンスデータの正当性を確認します
   * @param data {any} APIからのレスポンスデータ
   * @returns {boolean} true:問題なし
   */
  protected isItemListType(data: any): data is GetItemsResponse {
    return (
      data !== null &&
      typeof data === 'object' &&
      typeof data.items !== null &&
      typeof data.totalItems === 'number'
    );
  }
  /**
   * エラー種類に合ったエラーメッセージを整形
   * @param errCode
   * @returns { string } エラーメッセージ
   */
  private makeException(errCode: string): string {
    const err = errorConf.find(v => v.code === errCode);
    return err !== null ? `[${err?.code}]${err?.message}` : '';
  }


  /**
   * 該当文字列がEmptyでないか確認
   * @param data - 確認対象文字列
   * @returns bool
   */
  isCheckVal(data:string):boolean {
    if (
      data !== null &&
      data !== undefined &&
      typeof data === 'string' &&
      data.length > 0
    ) return true;
    return false;
  }

  /**
   * 
   * フォーム自動生成時にHexabaseUIから取得したデータに存在しないフォームを
   * form.tsなどに追加したフォームデータでフラグがある場合にフォーム追加する処理
   * @module addDataNotExistHexabase
   * @param checkData - Hexabaseから取得したデータ
   * @param optionalForm - フォーム内容
   * @returns void
   */
  public addDataNotExistHexabase(checkData: Array<any>, optionalForm: Array<any>) {
    for (const form of optionalForm) {
      if (form.notExistHexabase === true) {
        checkData.push(
          Object({
            display_id: form.display_id,
            props: form.props
          })
        );
      }
    }
  }

  /**
   * 該当するprop内のnameを検索し、一致するインデックス番号を返す
   * @module getDetailFieldsDataDeepIndex
   * @param {any} data - 検索する明細データ
   * @param {any} valueName - 検索に使用されるName
   * @return deepIndex - インデックス番号
   */
  public getDetailFieldsDataDeepIndex(data: Array<FieldChildrenProp>, valueName: string) {
    const deepIndex = data.findIndex(v => {
      if (v.props) {
        return v.props.name === valueName;
      }
      return false;
    });
    return deepIndex;
  }

  /**
   * 指定されたアイテムのファイルを削除
   * @module deleteFile
   * @param {any} fileId - 削除するファイルID
   * @return response - hexabaseAPIのdeleteFileレスポンス
   */
  public deleteFile(
    fileId: string
  ) {
    return this.hexaApi
      .deleteFile(authState.token,fileId)
      .then(response => {
        if (response) {
          return response;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 指定されたファイルIDのファイルをダウンロード
   * @module getPdfFileDownload
   * @param {any} fileId - ダウンロードするファイルID
   * @param {any} fileName - 使用するアイテムID
   * @return response - hexabaseAPIのgetFileレスポンス
   */
  public getPdfFileDownload(fileId: string, fileName: string) {
    return this.hexaApi
      .getFile(authState.token, fileId)
      .then(response => {
        if (response) {
          const blob = response;
          const url = window.URL.createObjectURL(
            new Blob([blob], {
              type: 'application/pdf'
            })
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          return response;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 指定されたファイルIDのファイル(.xlsx)をダウンロード
   * @module getXlxsFileDownload
   * @param {any} fileId - ダウンロードするファイルID
   * @param {any} fileName - 使用するアイテムID
   * @return response - hexabaseAPIのgetFileレスポンス
   */
  public getXlxsFileDownload(fileId: string, fileName: string) {
    return this.hexaApi
      .getFile(authState.token, fileId)
      .then(response => {
        if (response) {
          const blob = response;
          const url = window.URL.createObjectURL(
            new Blob([blob], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          return response;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * 以下より検索関連メソッド
   */
  /**
   * 動的生成コンポーネントに渡すオブジェクト（設定）を生成
   * @returns 各フィールド情報を含んだ配列
   * [
   *  {
   *    as_title: true
   *    col: 0
   *    component: "InputArea"
   *    dataType: "text"
   *    display_id: "order_application_no"
   *    fieldIndex: 0
   *    field_id: "60050d23eb0c9000012d15ee"
   *    full_text: false
   *    max_value: ""
   *    min_value: ""
   *    name: "発注申請No"
   *    props: {__ob__: Observer}
   *    row: 0
   *    search: true
   *    show_list: true
   *    sizeX: 2
   *    sizeY: 1
   *    status: false
   *    title_order: 4
   *    unique: false
   *  }...
   *]
   */
  public createFieldsList(fieldsData: DatastoreFields, masterData: any): FieldChildrenProp[] {
    let result: any = [];    
    // 各フィールド情報を取得し適用するモジュールコンポーネントとprops情報を追加
    Object.keys(fieldsData.fields).forEach(key => {
      const copyObj = Object.create(fieldsData);
      const fieldType = copyObj.fields[key].dataType;
      if (fieldType in formMapDefineData) {
        copyObj.fields[key]['component'] = formMapDefineData[fieldType].component;
        copyObj.fields[key]['props'] = this.makeProps(
          formMapDefineData[fieldType].props,
          copyObj.fields[key]
        );
        copyObj.fields[key]['show'] = true;
        result.push(copyObj.fields[key]);
      }
      if (fieldType === 'status') {
        copyObj.fields[key]['show'] = true;
        copyObj.fields[key]['component'] = 'SelectStatus';
        result.push(copyObj.fields[key]);
      }
    });
    result = this.addOptionalFormField(result, JSON.parse(JSON.stringify(searchFormConstruct)))
      .map((field) => this.addOptionalMasterData(field, masterData));
    return this.getLayoutKeySort(result);
  }

  public async getStatusList(appId: string, dId: string){
    const data = await this.getItemSearchConditions(appId, dId);
    return data.result.filter(v => {
      if (v.display_id === 'Status') {
        return v.statuses === undefined ? [] : v.statuses.map(recode => {
          recode['text'] = recode['status_name'];
          recode['value'] = recode['status_id'];
        });
      }
    });
  }

  public createSearchConf(fieldsData: any, masterData:any){
    const data = this.createFieldsList(fieldsData, masterData);    
    return (data.length !== 0 ) ? data.filter(v => v.search === true) : [] ;
  }

  /**
   * ファイルをhexabaseにアップロードする
   * @param {FormData} formData  - フォームデータ
   * @returns APIレスポンス
   */
  public uploadFile( formData: FormData ) {
    return this.hexaApi
      .uploadFile(authState.token, formData)
      .then(response => {
        return response;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * ファイルダウンロード
   * @return {void} - なし
   */
  public formOutPutDownload(data:any, downLoadFileName?: string, downLoadFormat?: string, info?:any): void {
    const blob = data;
    const type = info.contentType;
    const downloadName = downLoadFileName ? `${downLoadFileName}.${downLoadFormat}` : info.name;

    const url = window.URL.createObjectURL(
      new Blob([blob], {
        type: type
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', downloadName);
    document.body.appendChild(link);
    link.click();
  }

  /**
   * ファイルidを指定しファイルをダウンロードします
   * @param {string} file_id - ファイルID
   * @returns APIレスポンス
   */
  public getFile( file_id: string ) {
    return this.hexaApi
      .getFile(authState.token, file_id)
      .then(response => {
        return response;
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * ファイルidを指定しファイルをダウンロードします
   * @param {string} fileId - ファイルID
   * @returns APIレスポンス
   */
  public getFileData( fileId: string, contentType: string) {
    return this.hexaApi
      .getFile(authState.token, fileId)
      .then(response => {
        if (response) {
          const blob = response;
          const data = new Blob([blob], {
            type: contentType
          });

          return data;
        } else {
          throw new Error(this.makeException(this.typeDifferentCode));
        }
      })
      .catch(error => {
        throw new Error(error.message);
      });
  }

  /**
   * マスタ参照型SelectAreaの登録初期値セット
   * @param {FieldChildrenProp} field - DBから取得した単一フィールド情報
   * @returns {FieldChildrenProp} - DBから取得した単一フィールド情報
   */
   public setPropsByLocalProperty(field: FieldChildrenProp, thisProperty: {[k: string]: any}){
    if ( field.props !== undefined) {
      const keys = Object.keys(thisProperty);
      for (const [key, value] of Object.entries(field.props)) {
        if (
          field.props[key] !== null &&
          typeof field.props[key] === 'object' &&
          'property' in field.props[key] &&
          field.props[key].property &&
          field.props[key].name !== undefined &&
          field.props[key].name !== null &&
          keys.includes(field.props[key].name)
        ) {
          const includeKey = field.props[key].name;

          field.props[key] = thisProperty[includeKey];
        }
      }
    }
    return field;
  }

  /**
   * 複数Itemアクションを実行する
   * @param appId - アプリケーションID
   * @param param - アクション実行時必要パラメーター
   */
  public executeBatchAction(appId: string, param: any) {
    return this.hexaApi
      .executeBatchAction(authState.token, appId, param)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw new Error(error.message);
      });
  }
}